import type { CalendarAppointment } from '@odo/shared-ui';
import { Avatar } from '../../Avatar';
import { AppointmentStatusChip } from './AppointmentStatusChip';
import { PracticeDoctorLabel } from '../../shared/PracticeDoctorLabel';

// Only the patient fields, so any patient-bearing row (appointment or VA) can render it.
export type AppointmentPatientSource = Pick<
    CalendarAppointment,
    | 'patientId'
    | 'patientName'
    | 'profilePictureUrl'
    | 'practiceId'
    | 'practiceName'
    | 'practiceDoctorName'
    | 'patientInternalStatus'
>;

interface AppointmentPatientBlockProps {
    appointment: AppointmentPatientSource;
    /** Makes the whole block a link (new tab), e.g. to the patient's work-queue case. */
    href?: string;
}

const BLOCK_CLASSES = 'flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-2.5';

// Read-only twin of the dashboard PatientPicker's selected row: practice and doctor under the
// name, the patient's status chip pinned right.
export function AppointmentPatientBlock({ appointment, href }: AppointmentPatientBlockProps) {
    const content = (
        <>
            <div className="h-8 w-8 shrink-0">
                <Avatar
                    user={{ name: appointment.patientName, email: appointment.patientId }}
                    imageURL={appointment.profilePictureUrl ?? undefined}
                />
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-gray-800 truncate">
                    {appointment.patientName}
                </div>
                <PracticeDoctorLabel
                    practiceId={appointment.practiceId}
                    practiceName={appointment.practiceName}
                    doctorName={appointment.practiceDoctorName}
                    format="practice-doctor"
                    className="block truncate text-xs mt-0.5"
                    toneClassName="text-gray-500"
                    tooltipSide="bottom"
                />
            </div>
            {/* Capped so a long status truncates (full text on hover) instead of squeezing the name. */}
            <AppointmentStatusChip
                statusId={appointment.patientInternalStatus}
                className="min-w-0 max-w-[55%]"
                fallback={<span className="text-xs text-gray-400 shrink-0">--</span>}
            />
        </>
    );
    if (!href) return <div className={BLOCK_CLASSES}>{content}</div>;
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open ${appointment.patientName} in the work queue`}
            className={`${BLOCK_CLASSES} hover:border-gray-300 hover:bg-gray-50 transition-colors duration-200`}
        >
            {content}
        </a>
    );
}
